import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { createServiceLogger } from '../utils/logger.util';
import { RetivutApiService } from './retivutApi.service';
import { BedekApiService } from './bedekApi.service';
import { forkJoin, of, Observable, Subscription } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { RETIVUT_PLACES_COUNTER } from '@/services/api-count';
import { NetworkService } from './network.service';
import { AuthenticationService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class PreloadService {
  private preloadQueue: { name: string; observable: Observable<any> }[] = [];
  private isPreloading = false;
  private subscriptions = new Subscription();
  private hasInitialized = false;
  private lastPreloadTime = 0;
  private readonly PRELOAD_COOLDOWN = 1000 * 60 * 30; // 30 minutes
  private logger = createServiceLogger('PreloadService');

  constructor(
    private apiService: ApiService,
    private retivutApiService: RetivutApiService,
    private bedekApiService: BedekApiService,
    private networkService: NetworkService,
    private authService: AuthenticationService,
  ) {}

  /**
   * Initialize preloading of all API data in the background
   */
  public initPreload(): void {
    const operationId = this.logger.startOperation('init_preload');

    // Only preload if authenticated
    if (!this.authService.isLoggedIn) {
      this.logger.info('Preload skipped - user not authenticated', {
        operation: 'preload_auth_skip',
      });
      this.logger.endOperation(operationId, { success: false, reason: 'not_authenticated' });
      return;
    }

    // Check if we've preloaded recently
    const now = Date.now();
    if (now - this.lastPreloadTime < this.PRELOAD_COOLDOWN) {
      this.logger.info('Preload skipped - cooldown period active', {
        operation: 'preload_cooldown_skip',
        metadata: { minutesSinceLastPreload: Math.round((now - this.lastPreloadTime) / 1000 / 60) },
      });
      this.logger.endOperation(operationId, { success: false, reason: 'cooldown_active' });
      return;
    }

    // Only initialize once per session
    if (this.hasInitialized) {
      this.logger.endOperation(operationId, { success: false, reason: 'already_initialized' });
      return;
    }
    this.hasInitialized = true;

    // Setup the queue
    this.setupPreloadQueue();

    // Start preloading only if online and there's something to preload
    if (this.networkService.isOnline() && this.preloadQueue.length > 0) {
      this.lastPreloadTime = now;
      this.processQueue();
      this.logger.endOperation(operationId, {
        success: true,
        queueSize: this.preloadQueue.length,
        isOnline: true,
      });
    } else {
      this.logger.endOperation(operationId, {
        success: false,
        reason: 'offline_or_empty_queue',
        isOnline: this.networkService.isOnline(),
        queueSize: this.preloadQueue.length,
      });
    }

    // Listen for online status changes to start preloading when coming back online
    this.subscriptions.add(
      this.networkService.getOnlineStatus().subscribe((online) => {
        if (online && !this.isPreloading && this.preloadQueue.length > 0) {
          this.processQueue();
        }
      }),
    );
  }

  /**
   * Setup the preload queue with all APIs that should be preloaded
   */
  private setupPreloadQueue(): void {
    // Only setup if queue is empty
    if (this.preloadQueue.length > 0) {
      return;
    }

    this.logger.info('Setting up preload queue', {
      operation: 'preload_queue_setup',
    });

    // Add all data to preload queue
    const preloadItems = [
      { name: 'retivut-places', obs: () => this.retivutApiService.loadPlaces() },
      { name: 'retivut-defects', obs: () => this.retivutApiService.loadDefects() },
      { name: 'bedek-places', obs: () => this.bedekApiService.loadPlaces() },
      { name: 'bedek-defects', obs: () => this.bedekApiService.loadDefects() },
      { name: 'bedek-categories', obs: () => this.bedekApiService.loadCategories() },
      { name: 'report-headers', obs: () => this.apiService.getHeaders() },
    ];

    preloadItems.forEach((item) => {
      this.preloadQueue.push({
        name: item.name,
        observable: item.obs().pipe(
          catchError((err) => {
            this.logger.error(
              'Error preloading item',
              {
                operation: 'preload_item_failed',
                metadata: { itemName: item.name },
              },
              err,
            );
            return of(null);
          }),
        ),
      });
    });
  }

  /**
   * Process the preload queue with a small delay between requests to avoid overloading
   */
  private processQueue(): void {
    if (this.preloadQueue.length === 0) {
      this.isPreloading = false;
      this.logger.info('All preloading completed', {
        operation: 'preload_queue_complete',
      });
      return;
    }

    this.isPreloading = true;

    // Take the next item from the queue
    const nextItem = this.preloadQueue.shift();
    if (!nextItem) {
      this.isPreloading = false;
      return;
    }

    // Execute the observable with a delay between requests
    const subscription = nextItem.observable.subscribe({
      next: () => {
        // Wait 1 second before processing the next item to avoid overloading
        setTimeout(() => this.processQueue(), 1000);
      },
      error: () => {
        // Continue processing even if one item fails
        setTimeout(() => this.processQueue(), 1000);
      },
    });

    this.subscriptions.add(subscription);
  }

  /**
   * Preload all data immediately (useful for manual refresh)
   */
  public preloadAll(): Observable<any> {
    // Create observables for all data we want to preload
    const preloadObservables = [
      this.retivutApiService.loadPlaces(),
      this.retivutApiService.loadDefects(),
      this.bedekApiService.loadPlaces(),
      this.bedekApiService.loadDefects(),
      this.bedekApiService.loadCategories(),
      this.apiService.getHeaders(),
    ];

    // Use forkJoin to execute all observables in parallel
    return forkJoin(
      preloadObservables.map((obs) =>
        obs.pipe(
          catchError((err) => {
            this.logger.error(
              'Error in preloadAll operation',
              {
                operation: 'preload_all_error',
              },
              err,
            );
            return of(null);
          }),
        ),
      ),
    );
  }

  /**
   * Clean up subscriptions
   */
  public destroy(): void {
    this.subscriptions.unsubscribe();
    this.preloadQueue = [];
    this.isPreloading = false;
    this.hasInitialized = false;
  }
}
